import React from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { Aperture, Blend, MoveHorizontal, MoveVertical, PenLine, Scan, SquareRoundCorner } from 'lucide-react';

import CommonSlider from '../../../components/common-slider/CommonSlider';
import { DEFAULT_TEXT_BACKGROUND, DEFAULT_TEXT_OUTLINE, DEFAULT_TEXT_SHADOW } from '../../config';
import { getBlockStyle } from '../../elements/text/textRuns';
import ColorSwatch from '../color-swatch/ColorSwatch';
import ToggleSwitch from '../toggle-switch/ToggleSwitch';

import scss from './TextToolbar.module.scss';

const getEffects = (fontSize) => {
  const scale = Math.max(1, fontSize / 40);
  const range = (base) => Math.round(base * scale);
  return [
    {
      id: 'shadow',
      labelKey: 'effectShadow',
      defaults: DEFAULT_TEXT_SHADOW,
      colorKey: 'color',
      sliders: [
        { key: 'blur', labelKey: 'effectBlur', icon: Aperture, min: 0, max: range(40) },
        { key: 'offsetX', labelKey: 'effectOffsetX', icon: MoveHorizontal, min: -range(40), max: range(40) },
        { key: 'offsetY', labelKey: 'effectOffsetY', icon: MoveVertical, min: -range(40), max: range(40) },
        { key: 'opacity', labelKey: 'opacity', icon: Blend, min: 0, max: 100, percent: true },
      ],
    },
    {
      id: 'outline',
      labelKey: 'effectOutline',
      defaults: DEFAULT_TEXT_OUTLINE,
      colorKey: 'color',
      sliders: [{ key: 'width', labelKey: 'effectThickness', icon: PenLine, min: 1, max: range(12) }],
    },
    {
      id: 'background',
      labelKey: 'effectBackground',
      defaults: DEFAULT_TEXT_BACKGROUND,
      colorKey: 'fill',
      sliders: [
        { key: 'padding', labelKey: 'effectPadding', icon: Scan, min: 0, max: range(48) },
        { key: 'cornerRadius', labelKey: 'effectRadius', icon: SquareRoundCorner, min: 0, max: range(40) },
        { key: 'opacity', labelKey: 'opacity', icon: Blend, min: 0, max: 100, percent: true },
        { key: 'blur', labelKey: 'effectBackdropBlur', icon: Aperture, min: 0, max: range(40) },
      ],
    },
  ];
};

const toSliderValue = (slider, value) => (slider.percent ? Math.round(value * 100) : value);

function EffectsMenu({ element, text }) {
  const { t } = useTranslation('mediaStudio');
  const effects = getEffects(getBlockStyle(element).fontSize);

  const valueOf = (effect) => ({ ...effect.defaults, ...(element[effect.id] || {}) });
  const set = (effect, changes, options) =>
    text.updateBlock(
      (target) => ({ [effect.id]: { ...effect.defaults, ...(target[effect.id] || {}), ...changes } }),
      options,
    );

  return (
    <div className={clsx(scss.menu, scss.panelMenu, scss.effectsMenu)}>
      {effects.map((effect) => {
        const value = valueOf(effect);
        return (
          <section key={effect.id} className={scss.effect}>
            <div className={scss.effectHeader}>
              <span>{t(effect.labelKey)}</span>
              {value.enabled ? (
                <ColorSwatch
                  small
                  value={value[effect.colorKey]}
                  title={t('effectColor')}
                  onChange={(color) => set(effect, { [effect.colorKey]: color }, { live: true })}
                  onDone={text.endGesture}
                />
              ) : null}
              <ToggleSwitch
                checked={Boolean(value.enabled)}
                label={t(effect.labelKey)}
                onChange={(enabled) => set(effect, { enabled })}
              />
            </div>
            {value.enabled
              ? effect.sliders.map((slider) => {
                  const resetValue = toSliderValue(slider, effect.defaults[slider.key]);
                  const mixed = text.isMixed(value[slider.key]);

                  return (
                    <CommonSlider
                      key={slider.key}
                      label={t(slider.labelKey)}
                      icon={slider.icon}
                      min={slider.min}
                      max={slider.max}
                      value={mixed ? resetValue : toSliderValue(slider, value[slider.key])}
                      resetValue={resetValue}
                      resetLabel={t('resetSlider')}
                      valueFormatter={mixed ? () => t('mixed') : slider.percent ? (v) => `${v}%` : undefined}
                      onChange={(next) =>
                        set(effect, { [slider.key]: slider.percent ? next / 100 : next }, { live: true })
                      }
                      onCommit={text.endGesture}
                    />
                  );
                })
              : null}
          </section>
        );
      })}
    </div>
  );
}

export default EffectsMenu;
